import { type ReactNode } from "react";

export type Column<T> = {
  key: string;
  header: string;
  /** Optional renderer; defaults to row[key] as string. */
  cell?: (row: T) => ReactNode;
  align?: "left" | "right";
  width?: string;
};

export type TableProps<T> = {
  columns: ReadonlyArray<Column<T>>;
  rows: ReadonlyArray<T>;
  empty?: ReactNode;
  onRowClick?: (row: T) => void;
};

export function Table<T extends Record<string, unknown>>({
  columns,
  rows,
  empty,
  onRowClick,
}: TableProps<T>) {
  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-line bg-canvas-2 p-10 text-center text-ink-soft text-sm">
        {empty ?? "Nothing to show yet."}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-line bg-canvas-2 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line bg-canvas/60">
              {columns.map((c) => (
                <th
                  key={c.key}
                  style={{ width: c.width }}
                  className={`text-${c.align ?? "left"} px-4 py-3 text-xs uppercase tracking-wider text-ink-soft font-medium`}
                >
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr
                key={i}
                className={`border-b border-line/60 last:border-0 ${
                  onRowClick ? "hover:bg-canvas cursor-pointer transition" : ""
                }`}
                onClick={() => onRowClick?.(row)}
              >
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={`px-4 py-3 text-${c.align ?? "left"} text-ink`}
                  >
                    {c.cell ? c.cell(row) : String(row[c.key] ?? "")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
